import { Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    AGENT_NOTE_DOMAINS,
    AGENT_NOTE_SOURCES,
    AGENT_NOTE_STATUSES,
    type AgentNotesSearch,
} from '@apps/web/features/admin_agent_notes/model/search_params';
import {
    DOMAIN_LABELS,
    SOURCE_LABELS,
    STATUS_LABELS,
} from '@apps/web/features/admin_agent_notes/ui/note_status';
import { RangeControl } from '@apps/web/features/admin_agent_notes/ui/range_control';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import { cn } from '@apps/web/lib/utils';
import { type AdminAgentNoteStatusCountRead } from '@libraries/typescript/api_client';

function findOption<T extends string>(
    options: ReadonlyArray<T>,
    value: string,
): T | undefined {
    return options.find((option) => option === value);
}

const ALL_FILTER_VALUE = 'all';
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Every filter writes to the URL through `onPatch`; the URL is the only state
 * owner. The search input keeps a local draft that debounces into the URL.
 */
export function NotesFilterBar({
    search,
    statusCounts,
    onPatch,
}: {
    readonly search: AgentNotesSearch;
    readonly statusCounts: Array<AdminAgentNoteStatusCountRead>;
    readonly onPatch: (patch: Partial<AgentNotesSearch>) => void;
}) {
    const routeSearchValue = search.search ?? '';
    const [searchDraft, setSearchDraft] = useState(routeSearchValue);
    const debouncedSearchDraft = useDebouncedValue(
        searchDraft,
        SEARCH_DEBOUNCE_MS,
    );
    const searchDraftEditedRef = useRef(false);

    // Sync the draft when the URL changes externally (back button, clear).
    useEffect(() => {
        searchDraftEditedRef.current = false;
        setSearchDraft(routeSearchValue);
    }, [routeSearchValue]);

    useEffect(() => {
        if (!searchDraftEditedRef.current) {
            return;
        }
        const nextSearch = debouncedSearchDraft.trim() || undefined;
        if ((routeSearchValue.trim() || undefined) === nextSearch) {
            return;
        }
        onPatch({ search: nextSearch, page: 1 });
    }, [debouncedSearchDraft, onPatch, routeSearchValue]);

    const hasFixedRange =
        search.created_after !== undefined ||
        search.created_before !== undefined;

    const countsByStatus = new Map(
        statusCounts.map((entry) => [entry.status, entry.count]),
    );
    const totalCount = statusCounts.reduce(
        (sum, entry) => sum + entry.count,
        0,
    );

    const hasActiveFilters =
        search.status !== undefined ||
        search.source !== undefined ||
        search.domain !== undefined ||
        search.organization_id !== undefined ||
        search.created_after !== undefined ||
        search.created_before !== undefined ||
        search.range !== undefined ||
        (search.search ?? '') !== '';

    return (
        <div className="flex flex-col gap-3">
            <div
                className="flex flex-wrap items-center gap-1 rounded-lg border p-1"
                role="group"
                aria-label="Filter by status"
            >
                <StatusSegment
                    label="All"
                    count={totalCount}
                    isSelected={search.status === undefined}
                    onClick={() => {
                        onPatch({ status: undefined, page: 1 });
                    }}
                />
                {AGENT_NOTE_STATUSES.map((status) => (
                    <StatusSegment
                        key={status}
                        label={STATUS_LABELS[status]}
                        count={countsByStatus.get(status) ?? 0}
                        isSelected={search.status === status}
                        onClick={() => {
                            onPatch({
                                status:
                                    search.status === status
                                        ? undefined
                                        : status,
                                page: 1,
                            });
                        }}
                    />
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <AdminOrgPicker
                    value={search.organization_id}
                    onValueChange={(value) => {
                        onPatch({ organization_id: value, page: 1 });
                    }}
                />
                <Select
                    value={search.source ?? ALL_FILTER_VALUE}
                    onValueChange={(value) => {
                        onPatch({
                            source:
                                value === ALL_FILTER_VALUE
                                    ? undefined
                                    : findOption(AGENT_NOTE_SOURCES, value),
                            page: 1,
                        });
                    }}
                >
                    <SelectTrigger size="sm" className="w-[150px]">
                        <SelectValue placeholder="All sources" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL_FILTER_VALUE}>
                            All sources
                        </SelectItem>
                        {AGENT_NOTE_SOURCES.map((source) => (
                            <SelectItem key={source} value={source}>
                                {SOURCE_LABELS[source]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={search.domain ?? ALL_FILTER_VALUE}
                    onValueChange={(value) => {
                        onPatch({
                            domain:
                                value === ALL_FILTER_VALUE
                                    ? undefined
                                    : findOption(AGENT_NOTE_DOMAINS, value),
                            page: 1,
                        });
                    }}
                >
                    <SelectTrigger size="sm" className="w-[190px]">
                        <SelectValue placeholder="All domains" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL_FILTER_VALUE}>
                            All domains
                        </SelectItem>
                        {AGENT_NOTE_DOMAINS.map((domain) => (
                            <SelectItem key={domain} value={domain}>
                                {DOMAIN_LABELS[domain]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <RangeControl
                    preset={search.range}
                    fixed={
                        hasFixedRange
                            ? {
                                  from: search.created_after,
                                  to: search.created_before,
                              }
                            : null
                    }
                    allowAllTime={true}
                    onChange={(selection) => {
                        if (selection.kind === 'all') {
                            onPatch({
                                range: undefined,
                                created_after: undefined,
                                created_before: undefined,
                                page: 1,
                            });
                        } else if (selection.kind === 'preset') {
                            onPatch({
                                range: selection.preset,
                                created_after: undefined,
                                created_before: undefined,
                                page: 1,
                            });
                        } else {
                            onPatch({
                                range: undefined,
                                created_after: selection.from,
                                created_before: selection.to,
                                page: 1,
                            });
                        }
                    }}
                />
                <div className="relative min-w-[240px] flex-1 basis-[240px]">
                    <Search className="text-muted-foreground absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" />
                    <Input
                        value={searchDraft}
                        placeholder="Search notes…"
                        className="h-8 pl-8"
                        aria-label="Search notes"
                        onChange={(event) => {
                            searchDraftEditedRef.current = true;
                            setSearchDraft(event.target.value);
                        }}
                    />
                </div>
                {hasActiveFilters && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs"
                        onClick={() => {
                            setSearchDraft('');
                            onPatch({
                                status: undefined,
                                source: undefined,
                                domain: undefined,
                                organization_id: undefined,
                                created_after: undefined,
                                created_before: undefined,
                                range: undefined,
                                search: undefined,
                                page: 1,
                            });
                        }}
                    >
                        Clear filters
                    </Button>
                )}
            </div>
        </div>
    );
}

function StatusSegment({
    label,
    count,
    isSelected,
    onClick,
}: {
    readonly label: string;
    readonly count: number;
    readonly isSelected: boolean;
    readonly onClick: () => void;
}) {
    return (
        <button
            type="button"
            aria-pressed={isSelected}
            className={cn(
                'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs',
                'transition-colors',
                isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
            onClick={onClick}
        >
            <span>{label}</span>
            <span className="tabular-nums opacity-70">{count}</span>
        </button>
    );
}
